/**
 * PhoneLauncher — Phone Mirror panel
 *
 * Talks to phone_mirror_server.py (default: http://localhost:8765)
 * All API calls use the /mirror/ prefix — zero conflict with the main /api/ namespace.
 *
 * User-tunable defaults live in the DEFAULTS block below.
 * Every parameter is exposed in the UI so the operator can adjust without touching code.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";

// ─── OPEN DEFAULTS — change these freely, all are overrideable in the UI ─────
const DEFAULTS = {
  /** Mirror server URL. Env var > runtime window inject > this fallback */
  serverUrl:
    (window as any).__PHONE_MIRROR_URL__ ||
    process.env.REACT_APP_PHONE_MIRROR_URL ||
    window.location.origin + "/phone-server",

  /** Wi-Fi ADB port (almost always 5555) */
  adbPort: 5555,

  // scrcpy quality
  bitrate:        "8M",
  maxFps:         60,
  maxSize:        0,       // 0 = no cap
  videoCodec:     "h264",  // h264 | h265 | av1
  audioEnabled:   true,
  audioCodec:     "opus",  // opus | aac | flac | raw
  stayAwake:      true,
  turnScreenOff:  false,
  showTouches:    false,
  crop:           "",      // "W:H:X:Y" or blank
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Types ─────────────────────────────────────────────────────────────────────
interface AdbDevice {
  serial:     string;
  model:      string;
  product:    string;
  transport:  "usb" | "wifi";
  authorized: boolean;
}

interface DebugStatus {
  serial:      string;
  model:       string;
  transport:   string;
  authorized:  boolean;
  adb_enabled: boolean | null;
  dev_options: boolean | null;
}

type ConnStatus = "idle" | "scanning" | "connected" | "error";
type Tab = "mirror" | "connect" | "quality" | "call" | "debug";

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  root: {
    display: "flex", flexDirection: "column" as const,
    height: "100%", minHeight: 0,
    background: "#0b1326", color: "#e2e8f0",
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: 13,
  },
  header: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 18px",
    borderBottom: "1px solid #1e293b",
    background: "#0f1f3d", flexShrink: 0,
  },
  tabBar: {
    display: "flex", gap: 2,
    padding: "6px 16px 0",
    borderBottom: "1px solid #1e293b",
    background: "#0a1628", flexShrink: 0,
  },
  body: { flex: 1, display: "flex", overflow: "hidden", minHeight: 0 },
  sidebar: {
    width: 240, borderLeft: "1px solid #1e293b",
    background: "#0c1a30",
    display: "flex", flexDirection: "column" as const,
    overflowY: "auto" as const, flexShrink: 0,
  },
  pane: { flex: 1, overflowY: "auto" as const, padding: 16 },
  section: { marginBottom: 20 },
  label: { fontSize: 11, fontWeight: 700, color: "#ffd6a5", textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8, display: "block" },
  input: {
    background: "#1e293b", border: "1px solid #334155",
    borderRadius: 6, color: "#e2e8f0",
    padding: "6px 10px", fontSize: 13, width: "100%", boxSizing: "border-box" as const,
  },
  select: {
    background: "#1e293b", border: "1px solid #334155",
    borderRadius: 6, color: "#e2e8f0",
    padding: "6px 10px", fontSize: 13, width: "100%", boxSizing: "border-box" as const,
  },
  btn: (variant: "primary" | "ghost" | "danger" | "success") => ({
    border: variant === "ghost" ? "1px solid #334155" : "none",
    borderRadius: 6, padding: "6px 14px",
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    background:
      variant === "primary" ? "#ffd6a5"
      : variant === "danger"  ? "#ef4444"
      : variant === "success" ? "#22c55e"
      : "#1e293b",
    color:
      variant === "primary" ? "#0b1326"
      : variant === "ghost"  ? "#94a3b8"
      : "#fff",
    flexShrink: 0,
  }),
  chip: (on: boolean) => ({
    display: "inline-block",
    padding: "2px 8px", borderRadius: 4,
    fontSize: 11, fontWeight: 700,
    background: on ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
    color: on ? "#4ade80" : "#f87171",
    border: `1px solid ${on ? "#22c55e44" : "#ef444444"}`,
  }),
  dot: (s: ConnStatus) => ({
    width: 9, height: 9, borderRadius: "50%", flexShrink: 0,
    background: s === "connected" ? "#22c55e" : s === "scanning" ? "#f59e0b" : s === "error" ? "#ef4444" : "#64748b",
    boxShadow: s === "connected" ? "0 0 6px #22c55e" : s === "scanning" ? "0 0 6px #f59e0b" : "none",
  }),
  row: { display: "flex", gap: 8, alignItems: "center", marginBottom: 10 },
  toggle: (on: boolean) => ({
    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
    background: on ? "#22c55e" : "#334155", position: "relative" as const,
    transition: "background 0.2s", flexShrink: 0, border: "none",
  }),
};

function Toggle({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: "#94a3b8" }}>{label}</span>
      <button style={S.toggle(value)} onClick={() => onChange(!value)}>
        <div style={{ position: "absolute", top: 3, left: value ? 18 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
      </button>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
const PhoneLauncher: React.FC = () => {
  // ── Server config (user-editable) ─────────────────────────────────────────
  const [serverUrl, setServerUrl]   = useState(DEFAULTS.serverUrl);
  const [urlDraft,  setUrlDraft]    = useState(DEFAULTS.serverUrl);

  // ── Connection state ───────────────────────────────────────────────────────
  const [devices,      setDevices]      = useState<AdbDevice[]>([]);
  const [activeSerial, setActiveSerial] = useState<string | null>(null);
  const [connStatus,   setConnStatus]   = useState<ConnStatus>("idle");
  const [scanError,    setScanError]    = useState<string | null>(null);

  // ── Wi-Fi connect ──────────────────────────────────────────────────────────
  const [phoneIp,    setPhoneIp]   = useState("");
  const [adbPort,    setAdbPort]   = useState(DEFAULTS.adbPort);
  const [wifiMsg,    setWifiMsg]   = useState<string | null>(null);
  const [wifiOk,     setWifiOk]    = useState<boolean | null>(null);

  // ── Debug check ───────────────────────────────────────────────────────────
  const [debugResults, setDebugResults] = useState<DebugStatus[] | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // ── scrcpy quality ─────────────────────────────────────────────────────────
  const [bitrate,       setBitrate]       = useState(DEFAULTS.bitrate);
  const [maxFps,        setMaxFps]        = useState(DEFAULTS.maxFps);
  const [maxSize,       setMaxSize]       = useState(DEFAULTS.maxSize);
  const [videoCodec,    setVideoCodec]    = useState(DEFAULTS.videoCodec);
  const [audioEnabled,  setAudioEnabled]  = useState(DEFAULTS.audioEnabled);
  const [audioCodec,    setAudioCodec]    = useState(DEFAULTS.audioCodec);
  const [stayAwake,     setStayAwake]     = useState(DEFAULTS.stayAwake);
  const [turnScreenOff, setTurnScreenOff] = useState(DEFAULTS.turnScreenOff);
  const [showTouches,   setShowTouches]   = useState(DEFAULTS.showTouches);
  const [crop,          setCrop]          = useState(DEFAULTS.crop);
  const [launchMsg,     setLaunchMsg]     = useState<string | null>(null);

  // ── Call controls ──────────────────────────────────────────────────────────
  const [dialInput,   setDialInput]   = useState("");
  const [inCall,      setInCall]      = useState(false);
  const [muted,       setMuted]       = useState(false);
  const [volume,      setVolume]      = useState(10);
  const [callStatus,  setCallStatus]  = useState<"idle" | "calling" | "active">("idle");

  // ── UI tabs ────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<Tab>("mirror");

  // ── API helpers ────────────────────────────────────────────────────────────
  const api = useCallback((path: string, opts?: RequestInit) =>
    fetch(serverUrl + "/mirror" + path, opts),
    [serverUrl]);

  const apiJSON = useCallback(async (path: string, body?: object) => {
    const res = await api(path, {
      method: body ? "POST" : "GET",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body:    body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [api]);

  // ── Device scan ────────────────────────────────────────────────────────────
  const scanDevices = useCallback(async () => {
    setConnStatus("scanning");
    setScanError(null);
    try {
      const data = await apiJSON("/devices");
      const list: AdbDevice[] = data.devices || [];
      if (data.error && list.length === 0) throw new Error(data.error);
      setDevices(list);
      setActiveSerial(prev => {
        const keep = list.find(d => d.serial === prev);
        return keep ? keep.serial : list[0]?.serial ?? null;
      });
      setConnStatus(list.length > 0 ? "connected" : "idle");
    } catch (err: any) {
      setScanError(err.message || "Cannot reach mirror server");
      setConnStatus("error");
      setDevices([]);
    }
  }, [apiJSON]);

  useEffect(() => {
    scanDevices();
    const id = setInterval(scanDevices, 12_000);
    return () => clearInterval(id);
  }, [scanDevices]);

  const applyServerUrl = () => {
    setServerUrl(urlDraft);
    setScanError(null);
    setConnStatus("idle");
  };

  // ── Wi-Fi connect ──────────────────────────────────────────────────────────
  const handleWifiConnect = async () => {
    if (!phoneIp.trim()) return;
    setWifiMsg("Connecting...");
    setWifiOk(null);
    try {
      const data = await apiJSON("/connect", { ip: phoneIp.trim(), port: adbPort });
      setWifiOk(data.success);
      setWifiMsg(data.output || (data.success ? "Connected!" : "Failed"));
      if (data.success) { setTimeout(scanDevices, 1500); }
    } catch (err: any) {
      setWifiOk(false);
      setWifiMsg(err.message);
    }
  };

  const handleTcpIpMode = async () => {
    if (!activeSerial) return;
    try {
      const data = await apiJSON(`/tcpip/${encodeURIComponent(activeSerial)}?port=${adbPort}`);
      setWifiMsg(data.hint || data.output);
      setWifiOk(true);
    } catch (err: any) {
      setWifiMsg(err.message);
      setWifiOk(false);
    }
  };

  const handleWifiDisconnect = async () => {
    if (!phoneIp.trim()) return;
    try {
      const data = await apiJSON("/disconnect", { ip: phoneIp.trim(), port: adbPort });
      setWifiMsg(data.output);
      setWifiOk(null);
      setTimeout(scanDevices, 1000);
    } catch (err: any) {
      setWifiMsg(err.message);
    }
  };

  // ── Debug check ────────────────────────────────────────────────────────────
  const handleDebugCheck = async (serial?: string) => {
    setDebugLoading(true);
    setDebugResults(null);
    try {
      const body: any = {};
      if (serial) body.serial = serial;
      const data = await apiJSON("/debug-check", body);
      setDebugResults(data.results || []);
    } catch (err: any) {
      setDebugResults([]);
    } finally {
      setDebugLoading(false);
    }
  };

  // ── scrcpy launch ──────────────────────────────────────────────────────────
  const handleLaunch = async () => {
    if (!activeSerial) return;
    setLaunchMsg("Launching scrcpy...");
    try {
      const data = await apiJSON("/launch", {
        serial:          activeSerial,
        bitrate,
        max_fps:         maxFps,
        max_size:        maxSize,
        video_codec:     videoCodec,
        audio:           audioEnabled,
        audio_codec:     audioCodec,
        stay_awake:      stayAwake,
        turn_screen_off: turnScreenOff,
        show_touches:    showTouches,
        crop,
      });
      setLaunchMsg(data.launched ? `scrcpy started (PID ${data.pid})` : "Failed to start");
    } catch (err: any) {
      setLaunchMsg("Error: " + err.message);
    }
  };

  const handleStopMirror = async () => {
    if (!activeSerial) return;
    try {
      await apiJSON(`/stop/${encodeURIComponent(activeSerial)}`);
      setLaunchMsg("Session stopped.");
    } catch { }
  };

  // ── Call helpers ───────────────────────────────────────────────────────────
  const serial_q = activeSerial ? `?serial=${encodeURIComponent(activeSerial)}` : "";

  const handleDial = async () => {
    if (!dialInput.trim() || !activeSerial) return;
    setCallStatus("calling");
    try {
      const res = await api(`/call${serial_q}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ number: dialInput.trim() }),
      });
      if (res.ok) { setInCall(true); setCallStatus("active"); }
      else setCallStatus("idle");
    } catch { setCallStatus("idle"); }
  };

  const handleEndCall    = async () => { await api(`/call/end${serial_q}`,  { method: "POST" }); setInCall(false); setMuted(false); setCallStatus("idle"); };
  const handleMute       = async () => { await api(`/call/mute${serial_q}`, { method: "POST" }); setMuted(m => !m); };
  const handleVolume     = async (v: number) => { setVolume(v); await api(`/call/volume${serial_q}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ level: v }) }); };

  // ── Derived ────────────────────────────────────────────────────────────────
  const activeDevice = devices.find(d => d.serial === activeSerial) ?? null;

  const statusLabel =
    connStatus === "connected"  ? (activeDevice?.model ?? "Connected")
    : connStatus === "scanning" ? "Scanning…"
    : connStatus === "error"    ? "Server unreachable"
    : "No device";

  // ── Tab renderer ───────────────────────────────────────────────────────────
  const TABS: { id: Tab; label: string }[] = [
    { id: "mirror",  label: "Mirror" },
    { id: "connect", label: "Wi-Fi Connect" },
    { id: "quality", label: "Quality" },
    { id: "call",    label: "Call" },
    { id: "debug",   label: "Debug Check" },
  ];

  const tabStyle = (id: Tab): React.CSSProperties => ({
    padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    border: "none", borderBottom: tab === id ? "2px solid #ffd6a5" : "2px solid transparent",
    background: "transparent", color: tab === id ? "#ffd6a5" : "#64748b",
    borderRadius: "4px 4px 0 0",
  });

  return (
    <div style={S.root}>

      {/* ── Header ── */}
      <div style={S.header}>
        <span style={{ fontSize: 18 }}>📱</span>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Phone Mirror</span>
        <span style={{ background: "#1e293b", borderRadius: 4, padding: "2px 8px", fontSize: 11, color: "#94a3b8" }}>scrcpy · ADB</span>
        <div style={S.dot(connStatus)} />
        <span style={{ fontSize: 12, color: connStatus === "connected" ? "#4ade80" : connStatus === "scanning" ? "#fbbf24" : connStatus === "error" ? "#f87171" : "#64748b" }}>
          {statusLabel}
        </span>

        <div style={{ flex: 1 }} />

        {/* Server URL — user-editable */}
        <input
          value={urlDraft}
          onChange={e => setUrlDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && applyServerUrl()}
          placeholder={window.location.origin + "/phone-server"}
          title="Phone Mirror Server URL"
          style={{ ...S.input, width: 230 }}
        />
        <button style={S.btn("primary")} onClick={applyServerUrl}>Connect</button>
        <button style={S.btn("ghost")}   onClick={scanDevices}>⟳ Refresh</button>
      </div>

      {/* ── Tab bar ── */}
      <div style={S.tabBar}>
        {TABS.map(t => (
          <button key={t.id} style={tabStyle(t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ── Body ── */}
      <div style={S.body}>
        <div style={S.pane}>

          {/* ── MIRROR TAB ── */}
          {tab === "mirror" && (
            <>
              {scanError ? (
                <div style={{ textAlign: "center", paddingTop: 40 }}>
                  <div style={{ fontSize: 44 }}>📵</div>
                  <div style={{ fontWeight: 700, fontSize: 17, margin: "12px 0 6px" }}>Server Not Reachable</div>
                  <div style={{ color: "#94a3b8", marginBottom: 12 }}>{scanError}</div>
                  <pre style={{ background: "#1e293b", borderRadius: 8, padding: "12px 18px", fontSize: 12, color: "#ffd6a5", display: "inline-block", textAlign: "left" }}>
{"# Start the mirror server:\npython phone_mirror_server.py\n\n# Or with custom port:\nMIRROR_PORT=8765 python phone_mirror_server.py"}
                  </pre>
                  <div style={{ marginTop: 16 }}>
                    <button style={S.btn("primary")} onClick={scanDevices}>Retry</button>
                  </div>
                </div>
              ) : devices.length === 0 ? (
                <div style={{ textAlign: "center", paddingTop: 40 }}>
                  <div style={{ fontSize: 44 }}>🔌</div>
                  <div style={{ fontWeight: 700, fontSize: 17, margin: "12px 0 6px" }}>No Device Connected</div>
                  <div style={{ color: "#94a3b8", marginBottom: 16, lineHeight: 1.8 }}>
                    Connect via USB and enable <strong>USB Debugging</strong>,<br />or use the <strong>Wi-Fi Connect</strong> tab.
                  </div>
                  <button style={S.btn("primary")} onClick={scanDevices}>⟳ Scan</button>
                </div>
              ) : (
                <>
                  <div style={S.section}>
                    <span style={S.label}>Launch Mirror</span>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <button
                        style={S.btn("success")}
                        onClick={handleLaunch}
                        disabled={!activeSerial}
                      >
                        ▶ Start scrcpy
                      </button>
                      <button style={S.btn("danger")} onClick={handleStopMirror} disabled={!activeSerial}>
                        ■ Stop
                      </button>
                    </div>
                    {launchMsg && (
                      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6, background: "#1e293b", padding: "6px 10px", borderRadius: 6 }}>
                        {launchMsg}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#475569", marginTop: 8 }}>
                      scrcpy opens a native window on this machine. Go to the <strong>Quality</strong> tab to tune bitrate, FPS, codec and more.
                    </div>
                  </div>

                  <div style={S.section}>
                    <span style={S.label}>Current Settings</span>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: 12, color: "#94a3b8" }}>
                      <span>Bitrate</span>       <span style={{ color: "#e2e8f0" }}>{bitrate}</span>
                      <span>Max FPS</span>       <span style={{ color: "#e2e8f0" }}>{maxFps}</span>
                      <span>Max size</span>      <span style={{ color: "#e2e8f0" }}>{maxSize || "native"}</span>
                      <span>Video codec</span>   <span style={{ color: "#e2e8f0" }}>{videoCodec}</span>
                      <span>Audio</span>         <span style={{ color: "#e2e8f0" }}>{audioEnabled ? audioCodec : "off"}</span>
                      <span>Stay awake</span>    <span style={{ color: "#e2e8f0" }}>{stayAwake ? "yes" : "no"}</span>
                      <span>Show touches</span>  <span style={{ color: "#e2e8f0" }}>{showTouches ? "yes" : "no"}</span>
                      {crop && <><span>Crop</span><span style={{ color: "#e2e8f0" }}>{crop}</span></>}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── WI-FI CONNECT TAB ── */}
          {tab === "connect" && (
            <>
              <div style={S.section}>
                <span style={S.label}>Step 1 — Enable TCP/IP mode (USB required once)</span>
                <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.8, marginBottom: 10 }}>
                  Connect via USB first, select the device in the sidebar, then click below to switch it to Wi-Fi mode.
                </div>
                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 80 }}>ADB Port</label>
                  <input
                    type="number"
                    value={adbPort}
                    onChange={e => setAdbPort(Number(e.target.value))}
                    style={{ ...S.input, width: 100 }}
                    min={1024} max={65535}
                  />
                  <button style={S.btn("ghost")} onClick={handleTcpIpMode} disabled={!activeSerial}>
                    Enable TCP/IP on {activeDevice?.model || "device"}
                  </button>
                </div>
              </div>

              <div style={S.section}>
                <span style={S.label}>Step 2 — Enter phone's IP address</span>
                <div style={S.row}>
                  <input
                    value={phoneIp}
                    onChange={e => setPhoneIp(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleWifiConnect()}
                    placeholder="192.168.1.42"
                    style={{ ...S.input, flex: 1 }}
                  />
                  <span style={{ color: "#475569" }}>:</span>
                  <input
                    type="number"
                    value={adbPort}
                    onChange={e => setAdbPort(Number(e.target.value))}
                    style={{ ...S.input, width: 80 }}
                    min={1024} max={65535}
                  />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btn("success")} onClick={handleWifiConnect} disabled={!phoneIp.trim()}>
                    Connect via Wi-Fi
                  </button>
                  <button style={S.btn("danger")} onClick={handleWifiDisconnect} disabled={!phoneIp.trim()}>
                    Disconnect
                  </button>
                </div>
                {wifiMsg && (
                  <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                    background: wifiOk === true ? "rgba(34,197,94,0.1)" : wifiOk === false ? "rgba(239,68,68,0.1)" : "#1e293b",
                    border: `1px solid ${wifiOk === true ? "#22c55e44" : wifiOk === false ? "#ef444444" : "#334155"}`,
                    color: "#e2e8f0" }}>
                    {wifiMsg}
                  </div>
                )}
              </div>

              <div style={S.section}>
                <span style={S.label}>Android 11+ — Wireless Debugging</span>
                <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.8 }}>
                  Go to <strong>Developer Options → Wireless Debugging</strong> and pair using the shown IP + port. No USB needed.
                </div>
              </div>
            </>
          )}

          {/* ── QUALITY TAB ── */}
          {tab === "quality" && (
            <>
              <div style={S.section}>
                <span style={S.label}>Video</span>

                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Bitrate</label>
                  <select value={bitrate} onChange={e => setBitrate(e.target.value)} style={{ ...S.select, flex: 1 }}>
                    {["2M","4M","8M","16M","24M","32M"].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <input
                    value={bitrate}
                    onChange={e => setBitrate(e.target.value)}
                    placeholder="e.g. 8M"
                    style={{ ...S.input, width: 80 }}
                    title="Custom bitrate"
                  />
                </div>

                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Max FPS</label>
                  <input
                    type="range" min={15} max={120} value={maxFps}
                    onChange={e => setMaxFps(Number(e.target.value))}
                    style={{ flex: 1, accentColor: "#ffd6a5" }}
                  />
                  <span style={{ fontSize: 12, color: "#e2e8f0", width: 36, textAlign: "right" }}>{maxFps}</span>
                </div>

                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Max Size (px)</label>
                  <input
                    type="number"
                    value={maxSize || ""}
                    onChange={e => setMaxSize(Number(e.target.value) || 0)}
                    placeholder="0 = native"
                    style={{ ...S.input, flex: 1 }}
                    min={0} step={100}
                  />
                </div>

                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Video Codec</label>
                  <select value={videoCodec} onChange={e => setVideoCodec(e.target.value)} style={{ ...S.select, flex: 1 }}>
                    <option value="h264">H.264 (default — most compatible)</option>
                    <option value="h265">H.265 (better quality, scrcpy ≥2.1)</option>
                    <option value="av1">AV1 (best compression, needs scrcpy ≥2.1)</option>
                  </select>
                </div>

                <div style={S.row}>
                  <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Crop (W:H:X:Y)</label>
                  <input
                    value={crop}
                    onChange={e => setCrop(e.target.value)}
                    placeholder="blank = full screen"
                    style={{ ...S.input, flex: 1 }}
                  />
                </div>
              </div>

              <div style={S.section}>
                <span style={S.label}>Audio</span>
                <Toggle value={audioEnabled} onChange={setAudioEnabled} label="Enable audio" />
                {audioEnabled && (
                  <div style={S.row}>
                    <label style={{ fontSize: 12, color: "#64748b", width: 110 }}>Audio Codec</label>
                    <select value={audioCodec} onChange={e => setAudioCodec(e.target.value)} style={{ ...S.select, flex: 1 }}>
                      <option value="opus">Opus (low latency)</option>
                      <option value="aac">AAC</option>
                      <option value="flac">FLAC (lossless)</option>
                      <option value="raw">Raw PCM</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={S.section}>
                <span style={S.label}>Behaviour</span>
                <Toggle value={stayAwake}     onChange={setStayAwake}     label="Stay awake while mirroring" />
                <Toggle value={turnScreenOff} onChange={setTurnScreenOff} label="Turn phone screen off (mirror only)" />
                <Toggle value={showTouches}   onChange={setShowTouches}   label="Show touch points" />
              </div>
            </>
          )}

          {/* ── CALL TAB ── */}
          {tab === "call" && (
            <div style={S.section}>
              <span style={S.label}>Dial via ADB</span>
              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 12, lineHeight: 1.8 }}>
                Initiates a call on the active phone using an ADB intent. The phone must be unlocked.
              </div>
              <div style={S.row}>
                <input
                  value={dialInput}
                  onChange={e => setDialInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !inCall && handleDial()}
                  placeholder="+971 50 123 4567"
                  style={{ ...S.input, flex: 1 }}
                  disabled={inCall}
                />
                {!inCall ? (
                  <button style={S.btn("success")} onClick={handleDial}
                    disabled={!dialInput.trim() || callStatus === "calling" || !activeSerial}>
                    {callStatus === "calling" ? "⏳" : "📞 Call"}
                  </button>
                ) : (
                  <button style={S.btn("danger")} onClick={handleEndCall}>📵 End</button>
                )}
              </div>

              {inCall && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12, padding: "12px 14px", background: "rgba(34,197,94,0.07)", border: "1px solid #22c55e33", borderRadius: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} />
                    <span style={{ fontWeight: 700, color: "#4ade80", fontSize: 12 }}>ON CALL — {dialInput}</span>
                  </div>
                  <div style={S.row}>
                    <button style={S.btn(muted ? "danger" : "ghost")} onClick={handleMute}>
                      {muted ? "🔇 Muted" : "🎙️ Mute"}
                    </button>
                    <span style={{ fontSize: 12, color: "#64748b" }}>🔊</span>
                    <input
                      type="range" min={0} max={15} value={volume}
                      onChange={e => handleVolume(Number(e.target.value))}
                      style={{ flex: 1, accentColor: "#ffd6a5" }}
                    />
                    <span style={{ fontSize: 11, color: "#64748b", width: 24 }}>{volume}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── DEBUG CHECK TAB ── */}
          {tab === "debug" && (
            <>
              <div style={S.section}>
                <span style={S.label}>USB Debugging Status</span>
                <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.8, marginBottom: 12 }}>
                  Checks each connected device for <strong>USB Debugging</strong> and <strong>Developer Options</strong> via ADB shell.
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button style={S.btn("primary")} onClick={() => handleDebugCheck()} disabled={debugLoading}>
                    {debugLoading ? "Checking…" : "Check All Devices"}
                  </button>
                  {activeSerial && (
                    <button style={S.btn("ghost")} onClick={() => handleDebugCheck(activeSerial)} disabled={debugLoading}>
                      Check {activeDevice?.model ?? "active"}
                    </button>
                  )}
                </div>

                {debugResults && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {debugResults.length === 0 ? (
                      <div style={{ color: "#475569" }}>No results — are devices connected?</div>
                    ) : debugResults.map(r => (
                      <div key={r.serial} style={{ background: "#1e293b", borderRadius: 8, padding: "12px 14px", border: "1px solid #334155" }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{r.model} <span style={{ fontSize: 11, color: "#64748b" }}>{r.serial}</span></div>
                        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "4px 12px", fontSize: 12 }}>
                          <span style={{ color: "#64748b" }}>Transport</span>
                          <span>{r.transport}</span>
                          <span style={{ color: "#64748b" }}>Authorized</span>
                          <span style={S.chip(r.authorized)}>{r.authorized ? "✓ Yes" : "✗ No — allow on phone"}</span>
                          <span style={{ color: "#64748b" }}>Developer Options</span>
                          <span style={S.chip(r.dev_options === true)}>{r.dev_options === null ? "unknown" : r.dev_options ? "✓ Enabled" : "✗ Disabled"}</span>
                          <span style={{ color: "#64748b" }}>USB Debugging</span>
                          <span style={S.chip(r.adb_enabled === true)}>{r.adb_enabled === null ? "unknown" : r.adb_enabled ? "✓ On" : "✗ Off"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={S.section}>
                <span style={S.label}>How to enable USB Debugging</span>
                {[
                  "Settings → About Phone → tap Build Number 7 times",
                  "Settings → Developer Options → enable USB Debugging",
                  "Connect via USB → allow debugging on the phone popup",
                  "For Wi-Fi: Settings → Developer Options → Wireless Debugging",
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#1e3a5f", color: "#93c5fd", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>{step}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Sidebar — device list ── */}
        <div style={S.sidebar}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #1e293b" }}>
            <span style={S.label}>Devices ({devices.length})</span>
            {devices.length === 0 ? (
              <div style={{ fontSize: 12, color: "#475569" }}>None found</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {devices.map(d => (
                  <div
                    key={d.serial}
                    onClick={() => setActiveSerial(d.serial)}
                    style={{
                      background: d.serial === activeSerial ? "rgba(34,197,94,0.08)" : "#1e293b",
                      border: "1px solid " + (d.serial === activeSerial ? "#22c55e44" : "#334155"),
                      borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 12, color: "#e2e8f0" }}>
                      {d.transport === "wifi" ? "📶" : "🔌"} {d.model}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{d.serial}</div>
                    {d.product && <div style={{ fontSize: 10, color: "#475569" }}>{d.product}</div>}
                    <div style={{ marginTop: 4 }}>
                      <span style={S.chip(d.authorized)}>{d.authorized ? "auth" : "! allow"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button style={{ ...S.btn("ghost"), flex: 1, fontSize: 11 }} onClick={scanDevices}>⟳ Refresh</button>
            </div>
          </div>

          {activeDevice && (
            <div style={{ padding: "14px 16px" }}>
              <span style={S.label}>Active</span>
              <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.9 }}>
                <div><span style={{ color: "#64748b" }}>Model: </span>{activeDevice.model}</div>
                {activeDevice.product && <div><span style={{ color: "#64748b" }}>Product: </span>{activeDevice.product}</div>}
                <div><span style={{ color: "#64748b" }}>Serial: </span><span style={{ fontSize: 10 }}>{activeDevice.serial}</span></div>
                <div><span style={{ color: "#64748b" }}>Link: </span>{activeDevice.transport === "wifi" ? "Wi-Fi" : "USB"}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneLauncher;
